import React, { useState } from 'react';
import { Table, TableHead, TableBody, TableRow, MenuItem, Tooltip, Popover } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import QueryString from 'qs';
import moment from 'moment';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import CommonDatePicker from '../../../form-components/CommonDatePicker';
import SmsChargeModal from '../../member/modal/sms/SmsChargeModal';
import { getSmsBalance, getSmsSendHistoryList } from '../../../api/sms';
import {
  ControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchGroup,
  DateRangeGroup,
  PresetSelect,
  DateFieldWrapper,
  DateDivider,
  SearchInput,
  SearchButton,
  ChargeButton,
  TableWrapper,
  HeaderCell,
  BodyCell,
  ContentCell,
  DataRow,
  StatusText,
  AttachedImage,
  PaginationContainer,
  PageButton,
  NavButton,
  EllipsisText,
  TruncatedContent,
  ReceiverCountButton,
  RecipientPopoverContent,
  RecipientSummary,
  RecipientSummaryItem,
  RecipientScroll,
  RecipientItem,
  RecipientPhoneText,
  RecipientBadge,
} from './SmsSendHistoryStyled';

const COLUMNS = [
  { id: 'branchName', label: '지점명', width: '120px' },
  { id: 'registeredDate', label: '등록일시', width: '120px' },
  { id: 'sentDate', label: '발송일시', width: '120px' },
  { id: 'senderName', label: '발송자', width: '160px' },
  { id: 'senderNumber', label: '발송번호', width: '160px' },
  { id: 'receiver', label: '수신자', width: '160px' },
  { id: 'content', label: '발송내용' },
  { id: 'messageType', label: '발송종류', width: '120px' },
  { id: 'attachedImage', label: '첨부이미지', width: '120px' },
  { id: 'sendCount', label: '발송건수', width: '120px' },
  { id: 'successCount', label: '성공건수', width: '120px' },
  { id: 'sendStatus', label: '발송상태', width: '120px' },
];

const STATUS_MAP = {
  SUCCESS: '발송완료',
  SENT: '발송완료',
  COMPLETED: '발송완료',
  FAIL: '발송실패',
  FAILED: '발송실패',
  ERROR: '발송실패',
  PENDING: '발송대기',
  PARTIAL: '부분 발송',
};

// 수신자별 발송상태 배지 라벨 (recipients[].status)
const RECIPIENT_STATUS_LABEL = {
  SENT: '발송완료',
  FAILED: '실패',
  PENDING: '대기',
};

// 발송내용 전문 tooltip. 긴 LMS 본문은 높이가 화면을 넘겨 첫 줄이 잘리므로,
// 높이를 뷰포트 기준으로 제한하고 넘치면 tooltip 안에서 스크롤시킨다.
const TOOLTIP_SX = {
  maxHeight: '40vh',
  overflowY: 'auto',
  maxWidth: '420px',
};

// 백엔드 조회 상한이 12개월이라 '전체'도 최근 12개월로 맞춘다.
const ALL_RANGE_MONTHS = 12;

const PRESET_OPTIONS = [
  { value: 'all', label: '전체' },
  { value: 'today', label: '오늘' },
  { value: '7days', label: '최근 7일' },
  { value: '30days', label: '최근 30일' },
  { value: 'custom', label: '직접선택' },
];

const getDateRangeByPreset = preset => {
  const today = moment();
  switch (preset) {
    case '7days':
      return { from: today.clone().subtract(6, 'days').toDate(), to: today.toDate() };
    case '30days':
      return { from: today.clone().subtract(29, 'days').toDate(), to: today.toDate() };
    case 'all':
      return {
        from: today
          .clone()
          .subtract(ALL_RANGE_MONTHS - 1, 'months')
          .startOf('month')
          .toDate(),
        to: today.toDate(),
      };
    case 'today':
    default:
      return { from: today.toDate(), to: today.toDate() };
  }
};

const formatDateTime = value => {
  if (!value) return '-';
  const m = moment(value);
  if (!m.isValid()) return '-';
  return `${m.format('YYYY-MM-DD')}\n${m.format('HH:mm')}`;
};

const SmsSendHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const isSuperAdmin = user?.userRole === 'ROLE_SUPERADMIN';
  const branchId = user?.branch?.branchId;

  const initialRange = getDateRangeByPreset('today');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [preset, setPreset] = useState('today');
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);
  const [recipientAnchor, setRecipientAnchor] = useState(null);
  const [recipientList, setRecipientList] = useState([]);

  const appliedFrom = moment(appliedRange.from).format('YYYY-MM-DD');
  const appliedTo = moment(appliedRange.to).format('YYYY-MM-DD');

  const query = QueryString.stringify(
    {
      from: appliedFrom,
      to: appliedTo,
      // 총관리자는 전 지점, 지점 관리자는 자기 지점만
      branchId: isSuperAdmin ? undefined : branchId || undefined,
      page: page - 1,
      size: pageSize,
    },
    { skipNulls: true },
  );

  const { data: historyData } = useQuery({
    queryKey: ['smsSendHistory', appliedFrom, appliedTo, branchId, page, pageSize],
    queryFn: () => getSmsSendHistoryList(query, user?.token),
    enabled: !!user?.token,
  });

  const { data: balanceData } = useQuery({
    queryKey: ['smsBalance', branchId],
    queryFn: () => getSmsBalance(branchId, user?.token),
    enabled: !!branchId && !!user?.token,
  });

  const rawList = historyData?.items || [];
  const keyword = searchKeyword.trim().toLowerCase();

  const list = keyword
    ? rawList.filter(item => {
        const branchName = (item.branchName || user?.branch?.branchName || '').toLowerCase();
        const senderName = (item.senderName || '').toLowerCase();
        const receiverName = (item.recipientName || '').toLowerCase();
        const content = (item.message || '').toLowerCase();
        return (
          branchName.includes(keyword) ||
          senderName.includes(keyword) ||
          receiverName.includes(keyword) ||
          content.includes(keyword)
        );
      })
    : rawList;

  const totalCount = historyData?.totalCount || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const remainingBalance = balanceData?.balance ?? balanceData?.remainingCount ?? 0;

  const handlePresetChange = value => {
    setPreset(value);
    if (value !== 'custom') {
      const range = getDateRangeByPreset(value);
      setFromDate(range.from);
      setToDate(range.to);
      setAppliedRange(range);
      setPage(1);
    }
  };

  const handleSearch = () => {
    setSearchKeyword(searchValue);
    setAppliedRange({ from: fromDate, to: toDate });
    setPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  const openRecipients = (e, recipients) => {
    setRecipientAnchor(e.currentTarget);
    setRecipientList(recipients || []);
  };
  const closeRecipients = () => setRecipientAnchor(null);

  const renderReceiver = item => {
    const name = item.recipientName;
    const phone = item.recipientPhone;
    if (!name && !phone && item.recipientCount != null) {
      const recipients = item.recipients || [];
      if (recipients.length > 0) {
        return (
          <ReceiverCountButton onClick={e => openRecipients(e, recipients)}>
            {Number(item.recipientCount).toLocaleString()}
            {t('명')}
            <ArrowDropDownIcon sx={{ fontSize: 18 }} />
          </ReceiverCountButton>
        );
      }
      return `${Number(item.recipientCount).toLocaleString()} 명`;
    }
    if (name && phone) return `${name}\n(${phone})`;
    if (name) return name;
    if (phone) return phone;
    return '-';
  };

  const renderSender = item => item.senderName || '-';

  const renderCount = value => (value != null ? `${Number(value).toLocaleString()}건` : '-');

  const renderCellValue = item => columnId => {
    switch (columnId) {
      case 'branchName':
        return item.branchName || user?.branch?.branchName || '-';
      case 'registeredDate':
        return formatDateTime(item.registeredAt);
      case 'sentDate':
        return formatDateTime(item.sentAt);
      case 'senderName':
        return renderSender(item);
      case 'senderNumber':
        return item.callbackNo || '-';
      case 'receiver':
        return renderReceiver(item);
      case 'content':
        return (
          <Tooltip
            title={<span style={{ whiteSpace: 'pre-line' }}>{item.message || ''}</span>}
            arrow
            placement="top-start"
            componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}>
            <TruncatedContent>{item.message || '-'}</TruncatedContent>
          </Tooltip>
        );
      case 'messageType':
        return item.messageType || '-';
      case 'attachedImage': {
        const urls = item.attachedImageUrls || [];
        return urls.length > 0 ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {urls.map((url, i) => (
              <AttachedImage key={i} src={url} alt={`첨부 이미지 ${i + 1}`} />
            ))}
          </div>
        ) : (
          '-'
        );
      }
      case 'sendCount':
        return renderCount(item.totalCount);
      case 'successCount':
        return renderCount(item.successCount);
      case 'sendStatus':
        return (
          <StatusText status={item.status}>
            {t(STATUS_MAP[item.status] || item.status || '-')}
          </StatusText>
        );
      default:
        return '-';
    }
  };

  const renderPagination = () => {
    const maxVisible = 3;
    let startPage = Math.max(1, page - 1);
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) pages.push(i);

    const showLastEllipsis = endPage < totalPages - 1;
    const showLastPage = endPage < totalPages;

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {showLastEllipsis && <EllipsisText>...</EllipsisText>}
        {showLastPage && (
          <PageButton isActive={page === totalPages} onClick={() => setPage(totalPages)}>
            {totalPages}
          </PageButton>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  const sentCount = recipientList.filter(r => r.status === 'SENT').length;
  const failedCount = recipientList.filter(r => r.status === 'FAILED').length;
  const pendingCount = recipientList.filter(r => r.status === 'PENDING').length;

  return (
    <>
      <ControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>

        <SearchGroup>
          <DateRangeGroup>
            <PresetSelect value={preset} onChange={e => handlePresetChange(e.target.value)}>
              {PRESET_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </MenuItem>
              ))}
            </PresetSelect>
            <DateFieldWrapper>
              <CommonDatePicker
                placeholder="시작일"
                value={fromDate}
                onChange={date => {
                  setFromDate(date);
                  setPreset('custom');
                }}
              />
            </DateFieldWrapper>
            <DateDivider>~</DateDivider>
            <DateFieldWrapper>
              <CommonDatePicker
                placeholder="종료일"
                value={toDate}
                onChange={date => {
                  setToDate(date);
                  setPreset('custom');
                }}
                minDate={fromDate}
              />
            </DateFieldWrapper>
          </DateRangeGroup>

          <SearchInput
            placeholder={t('검색어를 입력해 주세요')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SearchButton onClick={handleSearch}>{t('검색')}</SearchButton>
        </SearchGroup>

        <ChargeButton onClick={() => setIsChargeModalOpen(true)}>{t('SMS 충전하기')}</ChargeButton>
      </ControlsRow>

      <TableWrapper>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <HeaderCell key={col.id} fixedWidth={col.width}>
                  {t(col.label)}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length > 0 ? (
              list.map((item, idx) => {
                const getValue = renderCellValue(item);
                return (
                  <DataRow key={item.id || idx}>
                    {COLUMNS.map(col => {
                      if (col.id === 'content') {
                        return <ContentCell key={col.id}>{getValue(col.id)}</ContentCell>;
                      }
                      return (
                        <BodyCell key={col.id} fixedWidth={col.width}>
                          {getValue(col.id)}
                        </BodyCell>
                      );
                    })}
                  </DataRow>
                );
              })
            ) : (
              <DataRow>
                <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                  {t('데이터가 없습니다')}
                </BodyCell>
              </DataRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {renderPagination()}

      {isChargeModalOpen && (
        <SmsChargeModal
          open={isChargeModalOpen}
          onClose={() => setIsChargeModalOpen(false)}
          remainingCount={remainingBalance}
          balance={remainingBalance}
        />
      )}

      <Popover
        open={Boolean(recipientAnchor)}
        anchorEl={recipientAnchor}
        onClose={closeRecipients}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: 1 } }}>
        <RecipientPopoverContent>
          <RecipientSummary>
            <span>
              {t('수신자')} {recipientList.length.toLocaleString()}
              {t('명')}
            </span>
            <RecipientSummaryItem tone="sent">
              {t('성공')} {sentCount.toLocaleString()}
            </RecipientSummaryItem>
            {failedCount > 0 && (
              <RecipientSummaryItem tone="failed">
                {t('실패')} {failedCount.toLocaleString()}
              </RecipientSummaryItem>
            )}
            {pendingCount > 0 && (
              <RecipientSummaryItem>
                {t('대기')} {pendingCount.toLocaleString()}
              </RecipientSummaryItem>
            )}
          </RecipientSummary>
          <RecipientScroll>
            {recipientList.map((r, i) => (
              <RecipientItem key={`${r.phoneNo}-${i}`}>
                <RecipientPhoneText>{r.phoneNo}</RecipientPhoneText>
                <RecipientBadge status={r.status}>
                  {t(RECIPIENT_STATUS_LABEL[r.status] || r.status)}
                </RecipientBadge>
              </RecipientItem>
            ))}
          </RecipientScroll>
        </RecipientPopoverContent>
      </Popover>
    </>
  );
};

export default SmsSendHistory;
